import React, { useContext, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthContext, UnreadMessagesContext } from '../../App';
import axios from 'axios';
import {
  LayoutDashboard,
  PlusCircle,
  Search,
  Briefcase,
  Calendar,
  Clock,
  Wallet,
  TrendingUp,
  Send,
  ArrowDownToLine,
  FileText,
  MessageSquare,
  Star,
  Users,
  AlertCircle,
  User,
  LogOut,
  Award,
  ChevronsLeft,
  ChevronsRight,
  Calculator,
  BarChart3,
  ShieldCheck
} from 'lucide-react';
// Tooltip handled via pure CSS .sidebar-tip
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import '../../styles/sidebar.css';
import { BACKEND_URL } from '../../config/api';

const API = `${BACKEND_URL}/api`;

const Sidebar = ({ isCollapsed, onToggle, onLogoutRequest }) => {
  const { user, setUser, logout } = useContext(AuthContext);
  const { unreadCount } = useContext(UnreadMessagesContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const triggerLogout = () => {
    if (onLogoutRequest) {
      onLogoutRequest();
    } else {
      setShowLogoutDialog(true);
    }
  };

  const rolePreference = user?.role_preference || 'both';
  const tabParam = searchParams.get('tab');
  const currentMode = (tabParam === 'client' || tabParam === 'hero') ? tabParam : (user?.primary_role || 'client');

  const showClientItems = rolePreference === 'client' || rolePreference === 'both';
  const showHeroItems = rolePreference === 'hero' || rolePreference === 'both';
  const showModeSwitcher = rolePreference === 'both';

  const handleModeSwitch = (mode) => {
    axios.post(`${API}/user/selected-mode`, { selected_mode: mode }, { withCredentials: true })
      .catch(err => console.error("Failed to save mode:", err));
    axios.post(`${API}/user/primary-role`, { primary_role: mode }, { withCredentials: true })
      .then(() => {
        if (user && user.primary_role !== mode) {
          setUser({ ...user, primary_role: mode });
        }
      }).catch(err => console.error("Failed to save preference:", err));
    navigate(`/dashboard?tab=${mode}`);
  };

  const handleLogout = async () => {
    setShowLogoutDialog(false);
    await logout();
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navGroups = [
    {
      id: 'overview',
      label: 'Overview',
      items: [
        { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', show: true },
        { label: 'Messages', icon: MessageSquare, path: '/messages', show: true, badge: unreadCount > 0 ? unreadCount : null },
        { label: 'Calendar', icon: Calendar, path: '/calendar', show: showHeroItems && currentMode === 'hero' },
        { label: 'Availability', icon: Clock, path: '/availability', show: showHeroItems && currentMode === 'hero' },
      ]
    },
    {
      id: 'work',
      label: 'Work',
      items: [
        { label: 'Create Job', icon: PlusCircle, path: '/create-job', show: showClientItems && currentMode === 'client' },
        { label: 'Browse Jobs', icon: Search, path: '/browse-jobs', show: showHeroItems && currentMode === 'hero' },
        { label: 'My Active Bids', icon: Briefcase, path: '/jobs-applied', show: showHeroItems && currentMode === 'hero' },
      ]
    },
    {
      id: 'finance',
      label: 'Finance',
      items: [
        { label: 'Wallet', icon: Wallet, path: '/wallet', show: true },
        { label: 'Earnings', icon: TrendingUp, path: '/my-earnings', show: showHeroItems && currentMode === 'hero' },
        { label: 'Transfers', icon: Send, path: '/money-transfers', show: true },
        { label: 'Transactions', icon: ArrowDownToLine, path: '/transactions', show: true },
        { label: 'Invoices', icon: FileText, path: '/invoices', show: true },
        { label: 'Fee Calculator', icon: Calculator, path: '/fee-calculator', show: true },
      ]
    },
    {
      id: 'community',
      label: 'Community',
      items: [
        { label: 'Reviews', icon: Star, path: '/reviews', show: true },
        { label: 'Performance', icon: BarChart3, path: '/performance', show: true },
        { label: 'Referrals', icon: Users, path: '/referrals', show: true },
      ]
    },
    {
      id: 'help',
      label: 'Help & Policies',
      items: [
        { label: 'Disputes', icon: AlertCircle, path: '/my-disputes', show: true },
        { label: 'Cancellation Policy', icon: ShieldCheck, path: '/cancellation-policy', show: true },
      ]
    },
  ];

  const NavItem = ({ item }) => {
    if (!item.show) return null;
    const Icon = item.icon;
    const active = isActive(item.path.split('?')[0]);
    const modeClass = currentMode === 'hero' ? 'hero-mode' : 'client-mode';

    const content = (
      <button
        className={`sidebar-nav-item ${active ? `active ${modeClass}` : ''}`}
        onClick={() => navigate(item.path)}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="nav-item-icon">
          <Icon size={20} strokeWidth={active ? 2 : 1.5} />
          {item.badge && <span className="nav-item-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
        </span>
        {!isCollapsed && <span className="nav-item-label">{item.label}</span>}
      </button>
    );

    if (isCollapsed) {
      return (
        <div className="sidebar-tip-wrap">
          {content}
          <span className="sidebar-tip">{item.label}</span>
        </div>
      );
    }
    return content;
  };

  return (
    <>
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`} data-testid="sidebar">
        {/* Logo + Toggle */}
        <div className="sidebar-header">
          {isCollapsed ? (
            <div className="sidebar-logo-collapsed" onClick={() => navigate('/dashboard')}>
              <img src="/favicon.png" alt="taskeri®" className="sidebar-logo-icon" />
            </div>
          ) : (
            <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
              <picture>
                <source srcSet="/logo-dark.webp" type="image/webp" />
                <img src="/logo-dark.png" alt="taskeri®" className="sidebar-logo-img" />
              </picture>
            </div>
          )}
          <button
            className="sidebar-toggle-btn"
            onClick={onToggle}
            data-testid="sidebar-toggle"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
        </div>

        {/* Mode Switcher */}
        {showModeSwitcher && !isCollapsed && (
          <div className="sidebar-mode-switcher" data-testid="sidebar-mode-switcher">
            <button
              className={`mode-btn ${currentMode === 'client' ? 'active client' : ''}`}
              onClick={() => handleModeSwitch('client')}
            >
              <Briefcase size={14} />
              <span>Client</span>
            </button>
            <button
              className={`mode-btn ${currentMode === 'hero' ? 'active hero' : ''}`}
              onClick={() => handleModeSwitch('hero')}
            >
              <Award size={14} />
              <span>Hero</span>
            </button>
          </div>
        )}
        {showModeSwitcher && isCollapsed && (
          <div className="sidebar-tip-wrap">
            <button
              className={`sidebar-collapsed-mode ${currentMode === 'hero' ? 'hero' : 'client'}`}
              onClick={() => handleModeSwitch(currentMode === 'client' ? 'hero' : 'client')}
              data-testid="sidebar-mode-toggle-collapsed"
            >
              {currentMode === 'client' ? <Briefcase size={18} /> : <Award size={18} />}
            </button>
            <span className="sidebar-tip">Switch to {currentMode === 'client' ? 'Hero' : 'Client'} Mode</span>
          </div>
        )}

        {/* Nav Groups */}
        <nav className="sidebar-nav">
          {navGroups.map(group => {
            const visibleItems = group.items.filter(item => item.show);
            if (visibleItems.length === 0) return null;
            return (
              <div className="sidebar-nav-group" key={group.id}>
                {!isCollapsed && <span className="nav-group-label">{group.label}</span>}
                {isCollapsed && <div className="nav-group-divider" />}
                {visibleItems.map(item => (
                  <NavItem key={item.path} item={item} />
                ))}
              </div>
            );
          })}
        </nav>

        {/* User Section + Footer */}
        <div className="sidebar-footer">
          {isCollapsed ? (
            <>
              <div className="sidebar-tip-wrap">
                <button className={`sidebar-nav-item ${isActive('/profile') ? 'active' : ''}`} onClick={() => navigate('/profile')}>
                  <span className="nav-item-icon">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="sidebar-user-avatar-sm" />
                    ) : (
                      <User size={20} strokeWidth={1.5} />
                    )}
                  </span>
                </button>
                <span className="sidebar-tip">Profile</span>
              </div>
              <div className="sidebar-tip-wrap">
                <button className="sidebar-nav-item logout-btn" onClick={triggerLogout} onTouchEnd={(e) => { e.preventDefault(); triggerLogout(); }} data-testid="sidebar-logout">
                  <span className="nav-item-icon"><LogOut size={20} strokeWidth={1.5} /></span>
                </button>
                <span className="sidebar-tip">Log out</span>
              </div>
            </>
          ) : (
            <>
              <div className="sidebar-user-section">
                <button className="sidebar-user-btn" onClick={() => navigate('/profile')} data-testid="sidebar-profile">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="sidebar-user-avatar" />
                  ) : (
                    <div className="sidebar-user-placeholder">{(user?.name || 'U')[0].toUpperCase()}</div>
                  )}
                  <div className="sidebar-user-info">
                    <span className="sidebar-user-name">{user?.name}</span>
                    <span className="sidebar-user-role">View Profile</span>
                  </div>
                </button>
                <button className="sidebar-logout-btn" onClick={triggerLogout} onTouchEnd={(e) => { e.preventDefault(); triggerLogout(); }} data-testid="sidebar-logout" title="Log out">
                  <LogOut size={18} />
                </button>
              </div>
              <div className="sidebar-copyright">
                &copy; 2026 taskeri® Technologies Inc.
              </div>
            </>
          )}
        </div>
      </aside>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={(open) => { if (!open) setShowLogoutDialog(false); }}>
        <AlertDialogContent className="logout-dialog" data-testid="logout-dialog" style={{ zIndex: 50001 }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontSize: '18px', fontWeight: 600, color: '#111827', WebkitTextFillColor: '#111827', background: 'none' }}>
              Sign out of taskeri<sup style={{fontSize:'0.55em'}}>®</sup>?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
              You'll need to sign in again to access your dashboard, messages, and ongoing jobs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: '8px' }}>
            <AlertDialogCancel
              data-testid="logout-cancel"
              style={{ borderRadius: '10px', fontWeight: 500, margin: 0 }}
            >
              Stay signed in
            </AlertDialogCancel>
            <button
              onClick={handleLogout}
              onTouchEnd={(e) => { e.preventDefault(); handleLogout(); }}
              data-testid="logout-confirm"
              style={{
                borderRadius: '10px',
                fontWeight: 500,
                background: '#dc2626',
                color: '#fff',
                margin: 0,
                padding: '8px 16px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Sign out
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Sidebar;
